// Audit log viewer and backups (task 27, PLAN §11): the API shapes the Admin → Audit log and Admin → Backups
// screens read, and the plain-language labels both use.
import type { DrillReport } from './drill';

/** Mirrors `AUDIT_ACTIONS` in packages/db (the column's enum). */
export const AUDIT_ACTION_LABELS = {
  view: 'Viewed',
  create: 'Created',
  update: 'Changed',
  delete: 'Deleted',
  merge: 'Merged',
  unmerge: 'Unmerged',
  export: 'Exported',
  ai_call: 'AI call',
  login: 'Signed in',
  logout: 'Signed out',
  admin: 'Admin action',
} as const;
export type AuditActionKey = keyof typeof AUDIT_ACTION_LABELS;
export const isAuditAction = (v: unknown): v is AuditActionKey => typeof v === 'string' && v in AUDIT_ACTION_LABELS;

/** Entries per page in the viewer. */
export const AUDIT_PAGE_SIZE = 50;
/** Entries stay in D1 this long, then move to the monthly archive in R2 (PLAN §11: "90 days hot"). */
export const AUDIT_HOT_DAYS = 90;

export interface AuditEntryView {
  id: string;
  at: number;
  action: AuditActionKey;
  target: string | null;
  meta: Record<string, unknown> | null;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  ip: string | null;
  ua: string | null;
  requestId: string | null;
}

export interface AuditFilters {
  userId?: string;
  action?: AuditActionKey;
  /** Words in the target, e.g. an entity id or `document:`. */
  target?: string;
  /** Inclusive day range, `YYYY-MM-DD` (UTC). */
  from?: string;
  to?: string;
}

export interface AuditPage {
  entries: AuditEntryView[];
  /** Pass back as `cursor` for the next (older) page; null on the last page. */
  next: string | null;
  /** Oldest entry still in D1 (older ones are in the archive). */
  oldestAt: number | null;
}

/**
 * One line for an entry: what happened to what. Explicit entries name their target (`entity:ID`, `user:ID`,
 * `admin:users`); automatic write entries carry the API path and the route pattern that answered.
 */
export function describeAuditEntry(e: Pick<AuditEntryView, 'action' | 'target' | 'meta'>): string {
  const verb = AUDIT_ACTION_LABELS[e.action] ?? e.action;
  const detail = typeof e.meta?.action === 'string' ? ` (${e.meta.action.replace(/_/g, ' ')})` : '';
  if (!e.target) return `${verb}${detail}`;
  const route = typeof e.meta?.route === 'string' ? e.meta.route : null;
  if (e.target.startsWith('/api/')) return `${verb} ${(route ?? e.target).replace(/^\/api\//, '')}${detail}`;
  const [kind, ...rest] = e.target.split(':');
  const what = kind?.replace(/[-_]/g, ' ') ?? '';
  return rest.length ? `${verb} ${what} ${rest.join(':')}${detail}` : `${verb} ${what}${detail}`;
}

export interface BackupObject {
  key: string;
  size: number;
  uploadedAt: number;
}

export interface BackupsStatus {
  environment: string;
  nightly: BackupObject[];
  preDeploy: BackupObject[];
  auditArchives: BackupObject[];
  audit: {
    /** Entries in D1 now. */
    hot: number;
    oldestAt: number | null;
    /** Entries older than this move to the archive at the next nightly run. */
    archiveBefore: number;
    /** Entries waiting to be archived. */
    due: number;
  };
  /** D1 Time Travel window (Cloudflare keeps it; restores are done from the command line, see the runbook). */
  timeTravelDays: number;
  /**
   * The newest weekly drill reports (task 48): the restore drill of this environment's backup, and the rollback
   * rehearsal (always on staging). Null until the first drill ran.
   */
  drills: { restore: DrillReport | null; rollback: DrillReport | null };
}

export interface AuditArchiveResult {
  archived: number;
  objects: string[];
  /** More entries were due than one run moves; the next run continues. */
  more: boolean;
}

/** First instant of the calendar month (UTC) that contains `ms`. */
export function monthStartUtc(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
}

/**
 * Entries created before this instant are archived: whole calendar months only, once the entire month is older
 * than the hot window, so each archive object holds one month and the viewer's range stays contiguous.
 */
export const auditArchiveBefore = (now: number) => monthStartUtc(now - AUDIT_HOT_DAYS * 86_400_000);

export const monthKey = (ms: number) => new Date(ms).toISOString().slice(0, 7);
